import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddTeamMutation as AddTeam } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddTeamMutation from './AddTeamMutation';
import ContactPicker, { Participant } from '../Contacts/ContactPicker';
import EventPicker from '../../Events/EventPicker';
import GetTeamTypesQuery from './GetTeamTypesQuery';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useProject from '../../../common/useProject';
import useQuery from '../../../api/useQuery';
import useValidation from '../../../common/useValidation';

const schema = yup.object({
  title: yup.string().required(),
  team_type: yup.object().required(),
  captain: yup.object().required(),
});

export interface AddTeamFormProps {
  eventId?: string;
  onSuccess?: (team: AddTeam['addTeam']) => void;
}

const AddTeamForm = ({ eventId: preselectEventId, onSuccess }: AddTeamFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
      team_type: null,
      captain: null,
    },
  });

  const [eventId, setEventId] = useState<string | null>(preselectEventId || null);
  const [participant, setParticipant] = useState<Participant>(null);

  const { data, loading: loadingTeamTypes } = useQuery(GetTeamTypesQuery,
    {
      variables: {
        project_id: project.id,
        event_id: eventId,
      },
      skip: !eventId,
      onCompleted: (data) => {
        if (data.project.event.id === eventId && data.project.event.team_types.length > 0) {
          form.set('team_type', { id: data.project.event.team_types[0].id });
        } else {
          form.set('team_type', null);
        }
      },
    },
  );

  const teamTypes = data?.project.event.team_types || [];

  const [addTeam, { error, loading }] = useMutation(AddTeamMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ addTeam }) => {
      notifier.success(t('notifications.team_added'));
      onSuccess?.(addTeam);
    },
  });

  const { valid, errors } = useValidation({
    schema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addTeam()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_team')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_team_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} attributes={{ title: t('team_name') }} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !!eventId, relative: true }}>
          <UI.InputLabel>
            {t('event')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <EventPicker
            eventId={eventId}
            onChange={(event) => setEventId(event?.id || null)}
            nullable={false}
          />
        </UI.InputGroup>

        {eventId && (
          <>
            <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
              <UI.InputLabel htmlFor="title">
                {t('team_name')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.DebouncedInput
                {...form.text('title')}
                maxLength={50}
                id="title"
              />
              <UI.ErrorMessages attribute={t('team_name')} errors={errors.title} />
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.team_type, touched: form.touched.team_type, relative: true }}>
              <UI.InputLabel htmlFor="AddTeamTeamType">
                {t('team_type')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              {loadingTeamTypes && (
                <UI.InputFallback />
              )}
              {teamTypes.length > 0 && !loadingTeamTypes && (
                <UI.Select
                  {...form.select(
                    'team_type.id',
                    teamTypes.map((teamType) => teamType.id),
                  )}
                  id="AddTeamTeamType"
                >
                  {teamTypes.map((teamTypes, index) => (
                    <option {...form.option(index)} key={index}>
                      {teamTypes.title || t('team')}
                    </option>
                  ))}
                </UI.Select>
              )}
              {teamTypes.length === 0 && !loadingTeamTypes && (
                <UI.Warning>
                  {t('no_team_types_added_yet')}
                  {' '}
                  <UI.Link to={`/${project.id}/events/${eventId}/settings/teams`}>
                    {t('manage_team_types')}
                  </UI.Link>
                </UI.Warning>
              )}
            </UI.InputGroup>

            <UI.InputGroup sc={{ valid: !errors.captain, touched: form.touched.captain }}>
              <UI.InputLabel>
                {t('team_captain')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <ContactPicker
                participant={participant}
                onChange={(participant) => {
                  setParticipant(participant);
                  form.set('captain.id', participant.id);
                }}
                nullable={false}
              />
              <UI.ErrorMessages attribute={t('team_captain')} errors={errors.captain} />
            </UI.InputGroup>
          </>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_team')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddTeamForm;
